import { useEffect, useMemo, useRef, useState } from "react";
import { useWorkspace } from "@/hooks/useWorkspace";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RichTextEditor } from "@/components/RichTextEditor";
import { LIGHT_SWATCHES, DARK_SWATCHES, SERIF_FONTS, SANS_FONTS, ensureFont } from "@/lib/excerptImageGenerator";
import { getCornerMarkMode, gradientToCss, gradientAvgLuminance, hexLuminance, effectiveLuminance } from "@/lib/tier";

import { UpgradeDialog } from "@/components/UpgradeDialog";
import { GradientBuilder } from "@/components/image-studio/excerpt-image/GradientBuilder";
import { GradientPresetManager } from "@/components/image-studio/excerpt-image/GradientPresetManager";
import { OverlayPanel } from "@/components/image-studio/excerpt-image/OverlayPanel";
import { Swatches, BrandSlots } from "@/components/image-studio/excerpt-image/SwatchControls";
import { usePinchPan } from "@/components/image-studio/excerpt-image/usePinchPan";
import { useLogoTinting } from "@/components/image-studio/excerpt-image/useLogoTinting";
import { usePreviewSizing } from "@/components/image-studio/excerpt-image/usePreviewSizing";
import { useExcerptImageExport } from "@/components/image-studio/excerpt-image/useExcerptImageExport";
import { useExcerptImageStudioState } from "@/components/image-studio/excerpt-image/useExcerptImageStudioState";
import { ExcerptImagePreviewPane } from "@/components/image-studio/excerpt-image/ExcerptImagePreviewPane";
import { useUsage } from "@/hooks/useUsage";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { HexColorPicker } from "react-colorful";

type ExcerptImageTabProps = {
  /** Renders the mobile sticky-header download button via portal-style callback. */
  renderHeaderActions?: (node: React.ReactNode) => void;
};

export const ExcerptImageTab = ({ renderHeaderActions }: ExcerptImageTabProps = {}) => {
  const { active } = useWorkspace();

  const { downloadsAtLimit, incrementDownload } = useUsage();
  const previewRef = useRef<HTMLDivElement>(null);

  const [canvasNaturalHeight, setCanvasNaturalHeight] = useState(1200);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => setCanvasNaturalHeight(el.scrollHeight || 1200);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { previewColumnRef, previewBoxRef, frameWidth, frameHeight, previewWidth, previewHeight } = usePreviewSizing(canvasNaturalHeight);
  const zoomSurfaceRef = useRef<HTMLDivElement>(null);
  const pinchPan = usePinchPan(zoomSurfaceRef);
  const [showHint, setShowHint] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState<{ tier: "Plus" | "Pro"; feature: string } | null>(null);

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!pinchPan.hintAt) return;
    setShowHint(true);
    const t = setTimeout(() => setShowHint(false), 1200);
    return () => clearTimeout(t);
  }, [pinchPan.hintAt]);

  const s = useExcerptImageStudioState(active);



  useEffect(() => {
    ensureFont(s.fontFamily);
    ensureFont("Inter");
  }, [s.fontFamily]);

  const textColor = useMemo(() => {
    const baseLum = s.bgMode === "gradient" ? gradientAvgLuminance(s.gradient) : hexLuminance(s.bgColor);
    const lum = s.bgMode === "gradient" && s.overlay.enabled
      ? effectiveLuminance(baseLum, s.overlay.color, s.overlay.opacity)
      : baseLum;
    return lum > 0.6 ? "#0F172A" : "#F5F5F5";
  }, [s.bgColor, s.bgMode, s.gradient, s.overlay.enabled, s.overlay.color, s.overlay.opacity]);

  const logoRenderUrl = useLogoTinting(s.logoPath, textColor);

  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => { void s.persistSettings(textColor); }, 600);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.bgMode, s.bgColor, textColor, s.fontFamily, s.fontStyle, s.displayUrl, s.logoPath, active, s.gradient, s.overlay]);

  const fontList = s.fontStyle === "serif" ? SERIF_FONTS : SANS_FONTS;
  const baseLum = s.bgMode === "gradient" ? gradientAvgLuminance(s.gradient) : hexLuminance(s.bgColor);
  const effLum = s.bgMode === "gradient" && s.overlay.enabled ? effectiveLuminance(baseLum, s.overlay.color, s.overlay.opacity) : baseLum;
  const isBgLight = effLum > 0.6;
  const dividerColor = isBgLight ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.18)";
  const fontStack = `"${s.fontFamily}", ${s.fontStyle === "serif" ? "Georgia, serif" : "Inter, system-ui, sans-serif"}`;
  const decoOpacity = 0.35;
  const logoOpacity = 0.5;
  const backgroundCss = useMemo(
    () => (s.bgMode === "gradient" ? gradientToCss(s.gradient) : s.bgColor),
    [s.bgMode, s.gradient, s.bgColor],
  );
  const exportBgFallback = s.bgMode === "gradient" ? (s.gradient.stops[0]?.hex ?? "#FFFFFF") : s.bgColor;
  const cornerMarkMode = getCornerMarkMode(s.logoPath);
  const isDefaultCornerMark = cornerMarkMode !== "custom";

  const logoStatusLabel = cornerMarkMode === "custom" ? "Currently showing: Your uploaded logo" : "Currently showing: Default Excerptus corner mark";

  const resetSignature = `${s.bodyHtml}\u0001${s.authorName}\u0001${s.pubTitle}\u0001${s.bgMode}\u0001${backgroundCss}`;
  const lastResetSig = useRef<string | null>(null);
  useEffect(() => {
    if (lastResetSig.current === null) {
      lastResetSig.current = resetSignature;
      return;
    }
    if (lastResetSig.current === resetSignature) return;
    lastResetSig.current = resetSignature;
    const didReset = pinchPan.reset();
    if (didReset && !isDesktop) {
      toast("Zoom reset", { description: "Preview zoom was reset after your edit." });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignature, pinchPan.reset, isDesktop]);

  const isOverflowing = canvasNaturalHeight > 1800;
  const { download, downloading } = useExcerptImageExport({
    previewRef,
    active,
    fontFamily: s.fontFamily,
    exportBgFallback,
    downloadsAtLimit,
    isOverflowing,
    incrementDownload,
    onUpgradeRequired: () => setUpgradeOpen({ tier: "Pro", feature: "Unlimited image downloads" }),
  });

  useEffect(() => {
    if (!renderHeaderActions) return;
    renderHeaderActions(
      <Button onClick={download} disabled={downloading || isOverflowing} size="sm" className="md:hidden shrink-0 px-3">
        <Download className="h-4 w-4" /> {downloading ? "Rendering…" : "Download"}
      </Button>
    );
    return () => renderHeaderActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloading, downloadsAtLimit, isOverflowing, active, s.bodyHtml, s.authorName, s.pubTitle, s.displayUrl, s.bgMode, s.bgColor, s.gradient, s.overlay, s.fontFamily, s.fontStyle, logoRenderUrl]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-3 sm:px-5 lg:grid lg:gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] lg:items-start lg:overflow-visible lg:px-5 lg:pb-4">
      <Card className="shadow-soft order-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:order-1 lg:h-full lg:flex-none lg:flex-col">
        <CardContent className="flex min-h-0 min-w-0 flex-1 flex-col py-0 lg:h-full lg:p-0">
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
            <div className="min-w-0 space-y-5 px-3 pb-5 pt-4 sm:px-5 lg:px-6 lg:py-5 lg:pr-4 lg:pb-10">
              <div>
                <Label>Excerpt</Label>
                <RichTextEditor value={s.bodyHtml} onChange={(html) => s.setBodyHtml(html)} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Author</Label>
                  <Input value={s.authorName} onChange={(e) => s.setAuthorName(e.target.value)} />
                </div>
                <div>
                  <Label>Publication</Label>
                  <Input value={s.pubTitle} onChange={(e) => s.setPubTitle(e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Display URL</Label>
                <Input value={s.displayUrl} onChange={(e) => s.setDisplayUrl(e.target.value)} placeholder="example.com" />
              </div>

              <div>
                <Label>Logo</Label>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  {s.logoUrl && <img src={s.logoUrl} alt="logo" className="h-10 w-10 rounded border border-border object-contain" />}
                  <Input aria-label="Upload logo" type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => e.target.files?.[0] && s.onLogoUpload(e.target.files[0])} />
                  {s.logoPath && (
                    <Button type="button" variant="ghost" size="sm" onClick={s.onLogoClear} className="shrink-0">
                      Clear
                    </Button>
                  )}
                </div>
                <p className="mt-1 text-xs font-medium text-foreground">{logoStatusLabel}</p>
                <p className="mt-1 text-xs text-muted-foreground">Leave empty to show the default Excerptus corner mark. Uploading your own logo replaces it. Clearing the upload restores the default Excerptus mark. Auto-tinted to match the background. Upload SVG or transparent PNG (≥240px tall) for crispest results.</p>
              </div>

              <div>
                <Label>Font</Label>
                <Tabs
                  value={s.fontStyle}
                  onValueChange={(v) => {
                    s.setFontStyle(v as "serif" | "sans");
                    s.setFontFamily(v === "serif" ? SERIF_FONTS[0] : SANS_FONTS[0]);
                  }}
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="serif">Serif</TabsTrigger>
                    <TabsTrigger value="sans">Sans</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="mt-2 flex flex-wrap gap-2">
                  {fontList.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => s.setFontFamily(f)}
                      onMouseEnter={() => ensureFont(f)}
                      style={{ fontFamily: f }}
                      className={`rounded-md border px-3 py-1.5 text-sm ${s.fontFamily === f ? "border-primary bg-primary/10" : "border-input hover:bg-muted"}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Background</Label>
                <Tabs
                  className="min-w-0"
                  value={s.bgMode}
                  onValueChange={(v) => {
                    const next = v as typeof s.bgMode;
                    s.setBgMode(next);
                  }}
                >
                  <TabsList className="grid h-auto w-full min-w-0 grid-cols-2 gap-1 p-1 sm:grid-cols-3 lg:grid-cols-5">
                    <TabsTrigger value="light" className="min-h-9 min-w-0 px-2 text-center text-xs whitespace-normal leading-tight">Light</TabsTrigger>
                    <TabsTrigger value="dark" className="min-h-9 min-w-0 px-2 text-center text-xs whitespace-normal leading-tight">Dark</TabsTrigger>
                    <TabsTrigger value="custom" className="min-h-9 min-w-0 px-2 text-center text-xs whitespace-normal leading-tight">
                      Custom
                    </TabsTrigger>
                    <TabsTrigger value="brand" className="min-h-9 min-w-0 px-2 text-center text-xs whitespace-normal leading-tight">
                      Brand
                    </TabsTrigger>
                    <TabsTrigger value="gradient" className="min-h-9 min-w-0 px-2 text-center text-xs whitespace-normal leading-tight">
                      Gradient
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="light">
                    <Swatches swatches={LIGHT_SWATCHES} active={s.bgColor} onSelect={s.setBgColor} />
                  </TabsContent>
                  <TabsContent value="dark">
                    <Swatches swatches={DARK_SWATCHES} active={s.bgColor} onSelect={s.setBgColor} />
                  </TabsContent>
                  <TabsContent value="custom" className="space-y-3 pt-3">
                    <HexColorPicker color={s.bgColor} onChange={s.setBgColor} style={{ width: "100%" }} />
                    <Input value={s.bgColor} onChange={(e) => s.setBgColor(e.target.value)} />
                  </TabsContent>
                  <TabsContent value="brand">
                    <BrandSlots slots={s.brandColors} activeHex={s.bgColor} onApply={s.setBgColor} onSave={s.saveBrandSlot} onDelete={s.deleteBrandSlot} />
                  </TabsContent>
                  <TabsContent value="gradient" className="min-w-0 space-y-3 overflow-hidden">
                    <GradientBuilder value={s.gradient} onChange={s.setGradient} />
                    <OverlayPanel value={s.overlay} onChange={s.setOverlay} />
                    <GradientPresetManager
                      customPresets={s.gradientPresets.map((preset) => ({ id: preset.id, name: preset.name, config: preset.gradient }))}
                      canManageCustomPresets={true}
                      savePresetName={s.presetName}
                      onSavePresetNameChange={s.setPresetName}
                      onSavePreset={s.saveGradientPreset}
                      onApplyCustomPreset={s.applyGradientPreset}
                      onDeleteCustomPreset={s.deleteGradientPreset}
                      isSavingPreset={s.savingPreset}
                      deletingPresetId={s.deletingPresetId}
                    />
                  </TabsContent>
                </Tabs>
              </div>

              <Button onClick={download} disabled={downloading || isOverflowing} className="hidden md:flex w-full">
                <Download className="h-4 w-4" /> {downloading ? "Rendering…" : "Download JPG"}
              </Button>
              <div aria-hidden className="hidden h-4 lg:block" />
            </div>
          </div>
        </CardContent>
      </Card>

      <ExcerptImagePreviewPane
        previewRef={previewRef}
        previewColumnRef={previewColumnRef}
        previewBoxRef={previewBoxRef}
        zoomSurfaceRef={zoomSurfaceRef}
        frameWidth={frameWidth}
        frameHeight={frameHeight}
        previewWidth={previewWidth}
        previewHeight={previewHeight}
        isDesktop={isDesktop}
        isOverflowing={isOverflowing}
        showHint={showHint}
        pinchPan={pinchPan}
        backgroundCss={backgroundCss}
        textColor={textColor}
        fontStack={fontStack}
        dividerColor={dividerColor}
        bgMode={s.bgMode}
        overlay={s.overlay}
        bodyHtml={s.bodyHtml}
        authorName={s.authorName}
        pubTitle={s.pubTitle}
        displayUrl={s.displayUrl}
        logoRenderUrl={logoRenderUrl}
        isDefaultCornerMark={isDefaultCornerMark}
        decoOpacity={decoOpacity}
        logoOpacity={logoOpacity}
      />
      <UpgradeDialog
        open={!!upgradeOpen}
        onOpenChange={(o) => !o && setUpgradeOpen(null)}
        tier={upgradeOpen?.tier ?? "Pro"}
        feature={upgradeOpen?.feature ?? ""}
      />
    </div>
  );
};

export default ExcerptImageTab;
